import React, { useState } from 'react';
import { DoubleLeftOutlined, DoubleRightOutlined, ReadOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Layout, Menu, Tooltip } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import wlLogo from '../assets/logo/wl-logo.svg';
import kdLogo from '../assets/logo/kd-logo.png';
import wlLogoSolo from '../assets/logo/wl-logo-solo.svg';
import waliaLogo from '../assets/logo/walia-coffee-logo.png';
import styles from './Sidebar.module.scss';
import { AccountTooltip } from './AccountTooltip';
import { useAuth } from '../hooks/useAuth';
import { useGetUserDetail } from '../hooks/useGetUserDetail';
import { getOrganizationUserIdFromToken } from '../helpers/jwtUtils';
import { useAppSelector } from '../store';
import { isKahveDiyariDomain, isWaliaCoffeeDomain } from '../helpers/domainUtils';
import SwitchComponent from '../components/SwitchComponent';
// SVG Icon imports
import onboardingIcon from '../assets/icons/onboarding.svg';
import heroBannerIcon from '../assets/icons/hero-banner.svg';
import engagementIcon from '../assets/icons/engagement.svg';
import faqIcon from '../assets/icons/faq.svg';
import legalTextIcon from '../assets/icons/legal-text.svg';
import campaignBlogIcon from '../assets/icons/campaign-blog.svg';
import reportsIcon from '../assets/icons/reports.svg';
import contentIcon from '../assets/icons/content.svg';
import brandingModuleIcon from '../assets/icons/branding-module.svg';
import consumerCardIcon from '../assets/icons/consumer-card.svg';
import notificationsIcon from '../assets/icons/notifications.svg';
import pushNotificationIcon from '../assets/icons/push-notification.svg';
import inAppNotificationsIcon from '../assets/icons/in-app-notifications.svg';
import branchesIcon from '../assets/icons/branches.svg';
import userManagementIcon from '../assets/icons/user-management.svg';
import userChartIcon from '../assets/icons/user-chart.svg';
import campaignReportsIcon from '../assets/icons/campaign-reports.svg';
import staticTextIcon from '../assets/icons/static-text.svg';
import productListIcon from '../assets/icons/product-list.svg';
import digitalMenuIcon from '../assets/icons/digital-menu.svg';
import categoryDefinitionsIcon from '../assets/icons/category-definitions.svg';

const { Sider } = Layout;

// SVG Icon wrapper component to ensure proper sizing and color inheritance
const SvgIcon: React.FC<{ src: string; className?: string }> = ({ src, className }) => {
  return (
    <img
      src={src}
      alt=""
      className={className}
      style={{
        width: '1.2em',
        height: '1.2em',
        display: 'inline-block',
        verticalAlign: 'middle',
      }}
    />
  );
};

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { accessToken } = useAuth();

  const organizationUserId = accessToken ? getOrganizationUserIdFromToken(accessToken) : null;

  const { data: userData, isLoading: isUserLoading } = useGetUserDetail(organizationUserId || '', { enabled: !!organizationUserId });
  const campaignListDropdown = useAppSelector((state) => state.campaigns.campaigns);
  const isKahveDiyari = isKahveDiyariDomain();
  const isWaliaCoffee = isWaliaCoffeeDomain();

  const contentChildren = [
    {
      key: '/onboarding',
      icon: <SvgIcon src={onboardingIcon} />,
      label: 'Onboarding Pages',
    },
    {
      key: '/hero-banner',
      icon: <SvgIcon src={heroBannerIcon} />,
      label: 'Hero Banner',
    },
    {
      key: '/engagement-card',
      icon: <SvgIcon src={engagementIcon} />,
      label: 'Engagement Card',
    },
    {
      key: '/faq',
      icon: <SvgIcon src={faqIcon} />,
      label: 'FAQ',
    },
    {
      key: '/legal-text',
      icon: <SvgIcon src={legalTextIcon} />,
      label: 'Legal Terms',
    },

    {
      key: '/campaigns',
      icon: <SvgIcon src={campaignBlogIcon} />,
      label: 'Campaigns',
    },
    {
      key: '/blogs',
      icon: <SvgIcon src={campaignBlogIcon} />,
      label: 'Blogs',
    },
  ];

  const menuItems: MenuProps['items'] = [
    {
      key: 'reports',
      icon: <SvgIcon src={reportsIcon} />,
      label: 'Reports',
      children: [
        {
          key: '/',
          icon: <SvgIcon src={userChartIcon} />,
          label: 'User Reports',
        },
        {
          key: 'campaign-analytics',
          icon: <SvgIcon src={campaignReportsIcon} />,
          label: 'Campaign Reports',
          children: campaignListDropdown?.map((campaign) => ({
            key: `/campaign-analytics/${campaign.id}`,
            icon: <ReadOutlined />,
            label: campaign.name,
          })),
        },
        {
          key: '/all-campaigns',
          icon: <SvgIcon src={campaignReportsIcon} />,
          label: 'All Campaigns',
        },
      ],
    },
    {
      key: 'content',
      icon: <SvgIcon src={contentIcon} />,
      label: 'Contents',
      children: contentChildren,
    },
    {
      key: 'Branding Module',
      icon: <SvgIcon src={brandingModuleIcon} />,
      label: 'Branding Module',
      children: [
        {
          key: '/static-texts',
          icon: <SvgIcon src={staticTextIcon} />,
          label: 'Static Texts',
        },
      ],
    },

    {
      key: '/consumer-card',
      icon: <SvgIcon src={consumerCardIcon} />,
      label: 'Consumers',
    },
    {
      key: '/notifications',
      icon: <SvgIcon src={notificationsIcon} />,
      label: 'Notifications',
      children: [
        {
          key: '/push-notification',
          icon: <SvgIcon src={pushNotificationIcon} />,
          label: 'Push Notifications',
        },
        {
          key: '/in-app-notification',
          icon: <SvgIcon src={inAppNotificationsIcon} />,
          label: 'In-App Notifications',
        },
      ],
    },

    {
      key: '/branches',
      icon: <SvgIcon src={branchesIcon} />,
      label: 'Branches',
    },
    {
      key: 'product-listing',
      icon: <SvgIcon src={productListIcon} />,
      label: 'Product Listing',
      children: [
        {
          key: '/digital-menu',
          icon: <SvgIcon src={digitalMenuIcon} />,
          label: 'Digital Menu',
        },
        {
          key: '/category-definitions',
          icon: <SvgIcon src={categoryDefinitionsIcon} />,
          label: 'Category Definitions',
        },
      ],
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    // Only navigate if the key starts with '/' (actual pages)
    if (key.startsWith('/')) {
      navigate(key);
    }
  };

  return (
    <Sider
      className={styles.siderStyle}
      width={300}
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
      collapsedWidth="80"
      trigger={null}
    >
      <div className={styles.mainContainer}>
        <div className={styles.logoContainer}>
          {collapsed ? (
            <img
              src={wlLogoSolo}
              alt="White Label Logo"
              className={styles.logoSolo}
            />
          ) : (
            <>
              <img
                src={wlLogo}
                alt="White Label Logo"
                className={styles.wlLogo}
              />
              {isKahveDiyari ? (
                <img
                  src={kdLogo}
                  alt="Kahve Diyarı Logo"
                  className={styles.kdLogo}
                />
              ) : null}
              {isWaliaCoffee ? (
                <img
                  src={waliaLogo}
                  alt="Walia Coffee Logo"
                  className={styles.waliaLogo}
                />
              ) : null}
            </>
          )}
        </div>

        <div className={styles.sidebarMenuContainer}>
          <div className={styles.sidebarMenu}>
            <Menu
              mode="inline"
              theme="dark"
              defaultSelectedKeys={['/']}
              defaultOpenKeys={['reports']}
              selectedKeys={[location.pathname]}
              items={menuItems}
              onClick={handleMenuClick}
              className={styles.menuStyle}
            />
          </div>
        </div>

        <section className={styles.sidebarFooter}>
          <button
            className={styles.collapseButton}
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
          </button>

          {!isKahveDiyari && (
            <Tooltip
              title={collapsed ? 'User Management' : ''}
              placement="right"
            >
              <button
                className={`${styles.userManagementItem} ${location.pathname === '/user-management' ? styles.selected : ''}`}
                onClick={() => navigate('/user-management')}
              >
                <SvgIcon
                  src={userManagementIcon}
                  className={styles.userManagementIcon}
                />
                {!collapsed && <span className={styles.userManagementLabel}>User Management</span>}
              </button>
            </Tooltip>
          )}

          <AccountTooltip>
            <div className={styles.profileSection}>
              <div className={styles.profileContent}>
                {isUserLoading ? (
                  <>
                    <div className={styles.skeletonAvatar}></div>
                    <div className={styles.profileDetails}>
                      <div className={styles.profileInfo}>
                        <div className={`${styles.skeletonText} ${styles.skeletonName}`}></div>
                        <div className={`${styles.skeletonText} ${styles.skeletonEmail}`}></div>
                      </div>
                      <div className={styles.profileActions}>
                        <button
                          className={styles.logoutButton}
                          title="Logout"
                        >
                          ...
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.profileAvatar}>
                      <span className={styles.profileInitials}>
                        {userData?.fullName
                          ? userData.fullName
                              .split(' ')
                              .map((name) => name.charAt(0))
                              .join('')
                              .toUpperCase()
                              .slice(0, 2)
                          : 'U'}
                      </span>
                      <div className={styles.statusIndicator}></div>
                    </div>

                    <div className={styles.profileDetails}>
                      <div className={styles.profileInfo}>
                        <h4 className={styles.profileName}>{userData?.fullName || 'User'}</h4>
                        <p className={styles.profileEmail}>{userData?.email || 'No email'}</p>
                      </div>

                      <div className={styles.profileActions}>
                        <button
                          className={styles.logoutButton}
                          title="Logout"
                        >
                          ...
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className={styles.versionSection}>
                <p className={styles.profileVersion}>v{__APP_VERSION__}</p>
              </div>
            </div>
          </AccountTooltip>
        </section>
      </div>
    </Sider>
  );
};

export default Sidebar;
